import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getMobileContext,
  isMobileAnnouncementTarget,
  NARROW_VIEWPORT_MEDIA_QUERY,
  type MobileContext,
} from "@/utils/deviceDetection";
import { getStoreUrlByPlatform } from "@/utils/deepLink";
import {
  computeTopAnnouncementBarVisible,
  dismissTopAnnouncement,
  dispatchTopAnnouncementVisibility,
  getSlidesAuthState,
  readTopAnnouncementDismissed,
  TOP_ANNOUNCEMENT_BAR_HEIGHT_CLASS,
} from "@/lib/layout/top-announcement-bar";
import {
  shouldShowAgencyAnnouncement,
  TOP_ANNOUNCEMENT_SLIDES,
  type TopAnnouncementSlideConfig,
} from "@/components/navbar/topAnnouncementSlides";

const ROTATION_MS = 7000;

const slideTransition = { duration: 0.38, ease: [0.4, 0, 0.2, 1] as const };

const verticalSlideVariants = {
  enter: (direction: number) => ({
    y: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { y: 0, opacity: 1 },
  exit: (direction: number) => ({
    y: direction > 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

const fadeSlideVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

export interface TopAnnouncementBarProps {
  isAuthenticated: boolean;
  userRole: string | null;
  /** Auth session still loading — keep guest slides to avoid content flicker. */
  authLoading?: boolean;
  className?: string;
}

const filterSlides = (
  slides: TopAnnouncementSlideConfig[],
  options: {
    isAuthenticated: boolean;
    isOwner: boolean;
    isAdmin: boolean;
    isDashboardSession: boolean;
    mobileCtx: MobileContext;
  }
): TopAnnouncementSlideConfig[] =>
  slides.filter((slide) => {
    if (options.mobileCtx.isStandalone && slide.id === "app") {
      return false;
    }
    if (slide.guestOnly && options.isAuthenticated) {
      return false;
    }
    if (slide.hideForOwner && options.isOwner) {
      return false;
    }
    if (
      slide.hideForAdminAndDashboards &&
      !shouldShowAgencyAnnouncement({
        isAdmin: options.isAdmin,
        isDashboardSession: options.isDashboardSession,
      })
    ) {
      return false;
    }
    if (slide.mobileOnly && !isMobileAnnouncementTarget(options.mobileCtx)) {
      return false;
    }
    return true;
  });

interface SlidePaneProps {
  slide: TopAnnouncementSlideConfig;
  storeHref?: string;
  isRtl: boolean;
  message: string;
  ctaLabel: string | null;
}

const SlidePane = ({ slide, storeHref, isRtl, message, ctaLabel }: SlidePaneProps) => {
  const rowClassName =
    "group flex h-full w-full min-w-0 items-center justify-center gap-2 px-3 sm:gap-3 sm:px-6 lg:px-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night focus-visible:ring-inset";

  const body = (
    <>
      <span className="min-w-0 flex-1 truncate text-center text-[12px] leading-snug text-text-primary sm:flex-none sm:text-[13px]">
        {message}
      </span>
      {ctaLabel && (
        <span className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-semibold text-green-night sm:gap-1 sm:text-[13px]">
          <span className="border-b border-transparent transition-colors group-hover:border-green-night/40">
            {ctaLabel}
          </span>
          <ArrowRight
            className={cn("h-3.5 w-3.5", isRtl && "rotate-180")}
            aria-hidden
          />
        </span>
      )}
    </>
  );

  if (slide.externalStore && storeHref) {
    return (
      <a href={storeHref} target="_blank" rel="noopener noreferrer" className={rowClassName}>
        {body}
      </a>
    );
  }

  if (slide.href) {
    return (
      <Link to={slide.href} className={rowClassName}>
        {body}
      </Link>
    );
  }

  return <div className={rowClassName}>{body}</div>;
};

export const TopAnnouncementBar = ({
  isAuthenticated,
  userRole,
  authLoading = false,
  className,
}: TopAnnouncementBarProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isRtl = i18n.dir() === "rtl";
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => readTopAnnouncementDismissed());
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const lastDispatchedVisible = useRef<boolean | null>(null);

  const isOwner = userRole === "owner";
  const isAdmin = userRole === "admin";
  const isDashboardSession = location.pathname.startsWith("/admin");
  const isAuthenticatedForSlides = getSlidesAuthState(isAuthenticated, authLoading);

  const [mobileCtx, setMobileCtx] = useState<MobileContext>(() =>
    typeof window === "undefined"
      ? {
          isMobileBrowser: false,
          isNarrowViewport: false,
          isStandalone: false,
          platform: "other",
        }
      : getMobileContext()
  );

  useEffect(() => {
    const syncMobileContext = () => setMobileCtx(getMobileContext());
    syncMobileContext();
    const mq = window.matchMedia(NARROW_VIEWPORT_MEDIA_QUERY);
    mq.addEventListener("change", syncMobileContext);
    window.addEventListener("orientationchange", syncMobileContext);
    return () => {
      mq.removeEventListener("change", syncMobileContext);
      window.removeEventListener("orientationchange", syncMobileContext);
    };
  }, []);

  const slides = useMemo(
    () =>
      filterSlides(TOP_ANNOUNCEMENT_SLIDES, {
        isAuthenticated: isAuthenticatedForSlides,
        isOwner,
        isAdmin,
        isDashboardSession,
        mobileCtx,
      }),
    [isAuthenticatedForSlides, isOwner, isAdmin, isDashboardSession, mobileCtx]
  );

  /** Presence pool — ignore auth/owner so visibility does not flip after login. */
  const slidesForPresence = useMemo(
    () =>
      filterSlides(TOP_ANNOUNCEMENT_SLIDES, {
        isAuthenticated: false,
        isOwner: false,
        isAdmin: false,
        isDashboardSession: false,
        mobileCtx,
      }),
    [mobileCtx]
  );

  const visible = computeTopAnnouncementBarVisible({
    isDismissed,
    hasSlides: slidesForPresence.length > 0,
  });
  const useFadeTransition =
    prefersReducedMotion || isMobileAnnouncementTarget(mobileCtx);
  const hasMultipleSlides = slides.length > 1;

  const advanceSlide = useCallback(
    (direction: 1 | -1) => {
      setSlideDirection(direction);
      setActiveIndex((prev) => (prev + direction + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    if (lastDispatchedVisible.current === visible) {
      return;
    }
    lastDispatchedVisible.current = visible;
    dispatchTopAnnouncementVisibility(visible);
  }, [visible]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!visible || !hasMultipleSlides || isPaused || prefersReducedMotion) {
      return;
    }
    const id = window.setInterval(() => advanceSlide(1), ROTATION_MS);
    return () => window.clearInterval(id);
  }, [visible, hasMultipleSlides, isPaused, prefersReducedMotion, advanceSlide]);

  useEffect(() => {
    setSlideDirection(1);
    setActiveIndex(0);
  }, [slides.length, location.pathname]);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    window.setTimeout(() => {
      dismissTopAnnouncement();
      setIsDismissed(true);
      setIsExiting(false);
    }, 200);
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      if (index === activeIndex) {
        return;
      }
      const forward = (index - activeIndex + slides.length) % slides.length;
      const backward = (activeIndex - index + slides.length) % slides.length;
      setSlideDirection(forward <= backward ? 1 : -1);
      setActiveIndex(index);
    },
    [activeIndex, slides.length]
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current === null || !hasMultipleSlides) {
      return;
    }
    const deltaY = (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current;
    if (Math.abs(deltaY) < 36) {
      touchStartY.current = null;
      return;
    }
    advanceSlide(deltaY < 0 ? 1 : -1);
    touchStartY.current = null;
  };

  if (!visible) {
    return null;
  }

  const slide = slides[activeIndex] ?? slides[0];
  const message = t(slide.messageKey);
  const ctaLabel = slide.ctaKey ? t(slide.ctaKey) : null;
  const storeHref = slide.externalStore
    ? getStoreUrlByPlatform(mobileCtx.platform)
    : undefined;
  const variants = useFadeTransition ? fadeSlideVariants : verticalSlideVariants;

  return (
    <div
      className={cn(
        "relative z-[2] w-full shrink-0 overflow-hidden border-b border-border/80 bg-scandi-surface shadow-sm",
        TOP_ANNOUNCEMENT_BAR_HEIGHT_CLASS,
        "transition-opacity duration-200 ease-out",
        isExiting && "opacity-0",
        className
      )}
      role="region"
      aria-label={t("topBar.ariaLabel")}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsPaused(false);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={cn("flex h-full min-h-0 items-stretch", isRtl && "flex-row-reverse")}>
        <div
          className="relative h-full min-h-0 min-w-0 flex-1 overflow-hidden"
          aria-live="polite"
          aria-atomic="true"
        >
          <AnimatePresence mode="wait" custom={slideDirection} initial={false}>
            <motion.div
              key={slide.id}
              custom={slideDirection}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
              className="absolute inset-0 flex h-full w-full items-center"
            >
              <SlidePane
                slide={slide}
                storeHref={storeHref}
                isRtl={isRtl}
                message={message}
                ctaLabel={ctaLabel}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <div
          className={cn(
            "flex shrink-0 self-center items-center gap-2 pe-2 ps-1 sm:pe-3 sm:ps-2",
            isRtl && "flex-row-reverse"
          )}
        >
          {hasMultipleSlides && (
            <div
              className="hidden items-center gap-1.5 sm:flex"
              role="tablist"
              aria-label={t("topBar.slideIndicatorsAria")}
            >
              {slides.map((s, index) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={t("topBar.goToSlide", { number: index + 1 })}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    "rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night focus-visible:ring-offset-1",
                    index === activeIndex
                      ? "h-1 w-3 bg-green-night"
                      : "h-1 w-1 bg-border hover:bg-text-tertiary"
                  )}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={handleDismiss}
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night"
            aria-label={t("topBar.dismissAria")}
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>

      {hasMultipleSlides && !prefersReducedMotion && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-border/60"
          aria-hidden
        >
          <div
            key={`${activeIndex}-${isPaused}`}
            className={cn(
              "h-full bg-green-night/50",
              !isPaused && "animate-rakb-top-bar-progress origin-left"
            )}
            style={
              isPaused ? undefined : { animationDuration: `${ROTATION_MS}ms` }
            }
          />
        </div>
      )}
    </div>
  );
};

export default TopAnnouncementBar;
